import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import storageManager from '../utils/storageManager.js';
import { getCountryFile } from '../utils/countryUtils.js';

const slugify = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function TripCard({ trip, isActive = false, onClick, onSwitch, onComplete, onDelete, onExport, onEdit }) {
  const { t, i18n } = useTranslation();
  const [coverImage, setCoverImage] = useState(null);
  const [cityIllustration, setCityIllustration] = useState(null);
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    loadCoverImage();
  }, [trip]);

  // City illustration from public/TRIP/img/{country}/{country}-{city}.webp.
  // Best-effort: if the file doesn't exist (or the city name doesn't match, e.g.
  // across languages) it silently falls back to the gradient + emoji.
  useEffect(() => {
    let cancelled = false;
    const city = trip?.destination?.city;
    const country = trip?.destination?.country;
    setCityIllustration(null);
    if (!city || !country) return;
    (async () => {
      const fileId = await getCountryFile(country).catch(() => null);
      if (!fileId || cancelled) return;
      const folder = fileId.replace(/_travel_guide$/, '').replace(/_/g, '-');
      const path = `./TRIP/img/${folder}/${folder}-${slugify(city)}.webp`;
      const img = new Image();
      img.onload = () => { if (!cancelled) setCityIllustration(path); };
      img.onerror = () => { if (!cancelled) setCityIllustration(null); };
      img.src = path;
    })();
    return () => { cancelled = true; };
  }, [trip]);

  const loadCoverImage = async () => {
    if (!trip.coverImage) return;

    try {
      const thumbnail = await storageManager.getThumbnail(trip.coverImage);
      if (thumbnail && thumbnail.blob) {
        const imageUrl = URL.createObjectURL(thumbnail.blob);
        setCoverImage(imageUrl);
      }
    } catch (error) {
      console.error('Failed to load cover image:', error);
    }
  };

  const getCountryEmoji = (country) => {
    const emojiMap = {
      'Spain': '🇪🇸',
      'España': '🇪🇸',
      'France': '🇫🇷',
      'Francia': '🇫🇷',
      'Italy': '🇮🇹',
      'Italia': '🇮🇹',
      'Portugal': '🇵🇹',
      'Germany': '🇩🇪',
      'Alemania': '🇩🇪',
      'UK': '🇬🇧',
      'England': '🇬🇧',
      'Netherlands': '🇳🇱',
      'Greece': '🇬🇷',
      'Grecia': '🇬🇷'
    };
    return emojiMap[country] || '🌍';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDuration = (dates) => {
    if (!dates || !dates.totalDays) return '';
    const days = dates.totalDays;
    return `${days} ${days === 1 ? t('tripCard.day') : t('tripCard.days')}`;
  };

  const calculateProgress = () => {
    if (!trip.dates || !trip.dates.currentDay || !trip.dates.totalDays) return 0;
    return Math.min((trip.dates.currentDay / trip.dates.totalDays) * 100, 100);
  };

  const getCoverStyle = () => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    ];

    // Use trip ID to consistently pick a gradient
    const index = trip.tripId ? parseInt(trip.tripId.slice(-1), 16) % gradients.length : 0;

    const cover = coverImage || cityIllustration;
    // User photos fill (cover); city illustrations are wide panoramas, so show
    // them whole (contain) with the warm base behind the letterbox.
    const isIllustration = !coverImage && !!cityIllustration;
    return {
      background: cover ? `url(${cover})` : gradients[index],
      backgroundSize: isIllustration ? 'contain' : 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      ...(isIllustration ? { backgroundColor: '#f5f1eb' } : {}),
    };
  };

  // Active trip card (larger, highlighted)
  if (isActive) {
    const progress = calculateProgress();

    return (
      <div
        onClick={onClick}
        style={{
          background: 'white',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(45, 90, 110, 0.22)',
          border: '3px solid #5B8FA8',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(45, 90, 110, 0.32)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(45, 90, 110, 0.22)';
        }}
      >
        {/* Cover — city illustration shows as a full-width banner at its natural
            (panoramic) height; user photo / gradient keep the fixed-height box. */}
        <div style={{ position: 'relative', background: '#f5f1eb' }}>
          {(!coverImage && cityIllustration) ? (
            <img
              src={cityIllustration}
              alt={trip.destination?.city || ''}
              style={{ width: '100%', display: 'block' }}
            />
          ) : (
            <div style={{
              ...getCoverStyle(),
              height: '180px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '60px'
            }}>
              {!coverImage && '🏰'}
            </div>
          )}
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 215, 0, 0.95)',
            color: '#333',
            padding: '6px 12px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            🔥 {t('myTrips.inProgress')}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          <h3 style={{
            fontSize: '24px',
            fontWeight: '900',
            color: '#333',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {trip.destination?.city || 'Unknown'} {getCountryEmoji(trip.destination?.country)}
          </h3>

          <p style={{
            fontSize: '13px',
            color: '#666',
            marginBottom: '16px'
          }}>
            {formatDate(trip.dates?.startDate)} - {formatDate(trip.dates?.endDate)} • {formatDuration(trip.dates)}
          </p>

          {/* Progress */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '12px',
              color: '#8B7355',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              📍 {t('tripCard.dayProgress', {
                current: trip.dates?.currentDay || 1,
                total: trip.dates?.totalDays || 1,
                percent: Math.round(progress)
              })}
            </div>
            <div style={{
              height: '8px',
              background: 'rgba(184, 160, 130, 0.2)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${progress}%`,
                background: '#C4714A',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '12px', color: '#8B7355', display: 'flex', alignItems: 'center', gap: '6px' }}>
              📝 <strong style={{ color: '#B8A082', fontWeight: '700' }}>{trip.stats?.totalMemories || 0}</strong> {t('tripCard.memories')}
            </div>
            <div style={{ fontSize: '12px', color: '#8B7355', display: 'flex', alignItems: 'center', gap: '6px' }}>
              📸 <strong style={{ color: '#B8A082', fontWeight: '700' }}>{trip.stats?.totalPhotos || 0}</strong> {t('tripCard.photos')}
            </div>
            <div style={{ fontSize: '12px', color: '#8B7355', display: 'flex', alignItems: 'center', gap: '6px' }}>
              💭 <strong style={{ color: '#B8A082', fontWeight: '700' }}>{trip.stats?.totalReflections || 0}</strong> {t('tripCard.reflections')}
            </div>
            <div style={{ fontSize: '12px', color: '#8B7355', display: 'flex', alignItems: 'center', gap: '6px' }}>
              🏆 <strong style={{ color: '#B8A082', fontWeight: '700' }}>{trip.stats?.completedChallenges || 0}</strong> {t('tripCard.challenges')}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto',
            gap: '8px'
          }}>
            <button
              onClick={onClick}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '14px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#A85B38';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#C4714A';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {t('tripCard.continue')}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onEdit) onEdit(trip);
              }}
              style={{
                background: 'white',
                color: '#3498DB',
                border: '2px solid #3498DB',
                padding: '14px 14px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#3498DB';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#3498DB';
              }}
              title="Edit trip"
            >
              ✎
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onComplete) onComplete(trip.tripId);
              }}
              style={{
                background: 'white',
                color: '#27AE60',
                border: '2px solid #27AE60',
                padding: '14px 14px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#27AE60';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#27AE60';
              }}
              title="Complete trip"
            >
              ✓
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(trip);
              }}
              style={{
                background: 'white',
                color: '#E74C3C',
                border: '2px solid #E74C3C',
                padding: '14px 14px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#E74C3C';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#E74C3C';
              }}
              title="Delete trip"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Past trip card (compact)
  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    >
      {/* Cover — illustration as a full-width banner; photo/gradient stay boxed. */}
      {(!coverImage && cityIllustration) ? (
        <img
          src={cityIllustration}
          alt={trip.destination?.city || ''}
          style={{ width: '100%', display: 'block', background: '#f5f1eb' }}
        />
      ) : (
        <div style={{
          ...getCoverStyle(),
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '40px'
        }}>
          {!coverImage && '🗼'}
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '16px' }}>
        <h4 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#333',
          marginBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {trip.destination?.city || 'Unknown'} {getCountryEmoji(trip.destination?.country)}
        </h4>

        <p style={{
          fontSize: '12px',
          color: '#666',
          marginBottom: '12px'
        }}>
          {formatDate(trip.dates?.startDate)} - {formatDate(trip.dates?.endDate)} • {formatDuration(trip.dates)}
        </p>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '11px', color: '#8B7355' }}>
            📝 {trip.stats?.totalMemories || 0} {t('tripCard.memories')}
          </span>
          <span style={{ fontSize: '11px', color: '#8B7355' }}>
            📸 {trip.stats?.totalPhotos || 0} {t('tripCard.photos')}
          </span>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          gap: '8px'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onSwitch) onSwitch(trip);
            }}
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#A85B38';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#C4714A';
              e.target.style.transform = 'translateY(0)';
            }}
            title="Activate this trip"
          >
            🔄 {t('myTrips.activateButton')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onEdit) onEdit(trip);
            }}
            style={{
              background: 'white',
              color: '#8E44AD',
              border: '2px solid #8E44AD',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#8E44AD';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#8E44AD';
            }}
            title="Edit trip"
          >
            ✎
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onExport) onExport(trip);
            }}
            style={{
              background: 'white',
              color: '#3498DB',
              border: '2px solid #3498DB',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#3498DB';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#3498DB';
            }}
            title="Export trip"
          >
            📥
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onDelete) onDelete(trip);
            }}
            style={{
              background: 'white',
              color: '#E74C3C',
              border: '2px solid #E74C3C',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#E74C3C';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#E74C3C';
            }}
            title="Delete trip"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
}

export default TripCard;
